import { Box } from '@chakra-ui/react';
import { offset, size, useFloating } from '@floating-ui/react';
import { memo, useCallback } from 'react';

import { ChangeEvent, MenuContent, MenuItem } from '@components/common/Menu';
import {
  CreateOrgPopoverContextProvider,
  useCreateOrgPopoverContext,
} from '@features/navigation/context';
import CreateOrgPopover from '@features/onboarding/CreateOrgPopover';
import { useCreateOrg } from 'hooks/useCreateOrg';
import Plus from 'vectors/Plus';

export const NEW_BUSINESS_MENU_ITEM_VALUE = 'new-business';
const CreateOrgMenuItem = () => {
  const [popoverContext, setPopoverContext] = useCreateOrgPopoverContext();
  const { refs, floatingStyles } = useFloating({
    open: popoverContext.isCreating,
    onOpenChange: (open) => setPopoverContext({ isCreating: open }),
    middleware: [
      offset(({ rects }) => {
        return -rects.reference.height / 2 - rects.floating.height / 2;
      }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
          });
        },
      }),
    ],
  });

  const onCancel = useCallback(() => {
    setPopoverContext({ isCreating: false });
  }, [setPopoverContext]);

  const { createOrg } = useCreateOrg({
    onCancel,
  });

  const openPopover = useCallback(() => {
    setPopoverContext({ isCreating: true });
  }, [setPopoverContext]);

  const onSelect = useCallback(
    (ev: ChangeEvent) => {
      ev.preventDefault();
      setPopoverContext({ isCreating: true });
    },
    [setPopoverContext],
  );

  return (
    <>
      <Box ref={refs.setReference} onClick={openPopover}>
        <MenuItem showSelected={false} value={NEW_BUSINESS_MENU_ITEM_VALUE} onSelect={onSelect}>
          <MenuContent value="New business" icon={<Plus />} />
        </MenuItem>
      </Box>
      {popoverContext.isCreating && (
        <CreateOrgPopover
          setFloatingReference={refs.setFloating}
          floatingStyles={floatingStyles}
          onCancel={onCancel}
          onCreateOrg={createOrg}
          inputHeight="100%"
        />
      )}
    </>
  );
};

const CreateOrgMenuItemWithProvider = () => {
  return (
    <CreateOrgPopoverContextProvider>
      <CreateOrgMenuItem />
    </CreateOrgPopoverContextProvider>
  );
};

export default memo(CreateOrgMenuItemWithProvider);
